import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { currentWorkspace, historyBase, workspacePath } from '@/lib/workspace';

// Route table mirrors docs/control-plane.md §7.1.
// Member-vs-admin gating lives in `beforeEach` below - see the meta.role check.
const routes: RouteRecordRaw[] = [
  // ─── Auth ──────────────────────────────────────────────
  { path: '/signin', name: 'signin',
    component: () => import('@/views/SigninView.vue'),
    meta: { public: true } },

  // OAuth-provider redirect landing. PUBLIC by design: the
  // provider redirect carries `?code=...&state=...` and the user's
  // SPA session may or may not still be present (cookies survive,
  // Pinia state doesn't after the full-page nav to github.com).
  // Auth lives server-side on /v1/oauth/callback/{slug}: the
  // sealed `state` blob carries the original tenant + user + a
  // session-bind hash, and the GitHub-issued `code` is single-
  // use and bound to the redirect_uri. Marking this route admin-
  // only would bounce unauthenticated callbacks to /signin and
  // mangle the query string (?next=...?code=...&state=... double-
  // encodes the inner separators). The OAuthCallbackView itself
  // just forwards code+state to the backend and renders the
  // verdict - it has no admin-only UI.
  { path: '/oauth/callback/:slug', name: 'oauth-callback',
    component: () => import('@/views/OAuthCallbackView.vue'),
    meta: { public: true }, props: true },

  // OAuth consent screen (Phase 2.2). A third-party app's authorize redirect
  // landed the browser here; the user IS signed in by now (the authorize
  // handler bounces through /signin first if not). PUBLIC so the router guard
  // doesn't re-bounce - the backend /v1/oauth/consent endpoints enforce the
  // session + that the pending request belongs to this user.
  { path: '/oauth/consent', name: 'oauth-consent',
    component: () => import('@/views/ConsentView.vue'),
    meta: { public: true } },

  // OAuth 2.0 Device Authorization verification (RFC 8628). The browser
  // extension's device-code sign-in points the user here with a `?user_code`.
  // PUBLIC: a fresh visitor may have no session yet - DeviceView handles sign-in
  // in-page (it opens /signin and re-checks once the cookie appears) and the
  // backend /v1/oauth/device endpoints enforce the session on the decision.
  { path: '/oauth/device', name: 'oauth-device',
    component: () => import('@/views/DeviceView.vue'),
    meta: { public: true } },

  // Magic-link sign-in landing (P3). PUBLIC: the email link carries the
  // single-use token and the user has no session yet. MagicView POSTs the
  // token to /v1/auth/magic/verify, which sets the session cookie.
  { path: '/auth/magic', name: 'magic-link',
    component: () => import('@/views/MagicView.vue'),
    meta: { public: true } },

  // Greenfield workspace signup (P4). PUBLIC: invite-gated, creates a new
  // tenant + emails the owner a magic link to finish setup.
  { path: '/signup', name: 'signup-workspace',
    component: () => import('@/views/SignupView.vue'),
    meta: { public: true } },

  // Slack-track entry (Onboarding v2 P3). PUBLIC: invite-before-OAuth - the
  // visitor validates an invite code, then hands off to the server-side Slack
  // install flow (which consumes the invite + binds the new workspace).
  { path: '/get-started/slack', name: 'get-started-slack',
    component: () => import('@/views/GetStartedSlackView.vue'),
    meta: { public: true } },

  // Teams track: invite -> Sign in with Microsoft (records a provision grant for
  // the verified org) -> download + install the app package.
  { path: '/get-started/teams', name: 'get-started-teams',
    component: () => import('@/views/GetStartedTeamsView.vue'),
    meta: { public: true } },

  // Dev-only test harness (controlplane /v1/dev/* endpoints 404 unless
  // HQ_DEV_TOOLS is on, so this page is inert on prod). Public so the
  // "create demo tenant" sign-in works without a prior session.
  { path: '/dev', name: 'dev-tools',
    component: () => import('@/views/DevToolsView.vue'),
    meta: { public: true } },

  // ─── Member surface (shared with admins; role-gated UI inside) ──
  // Home is the product front door for everyone: a launchpad (ask + agent
  // chips + continue + agents), NOT a metrics page. The old ops-stats
  // Dashboard is retired - those numbers live on the admin Usage page.
  { path: '/', name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { role: 'member' } },
  // Each lane is its own URL: /studio (agents, canonical), /studio/ask,
  // /studio/email. The constrained param means /studio/<uuid> still falls
  // through to the :id thread route below. Name stays 'studio-list' so every
  // existing { name: 'studio-list' } link resolves (lane absent => agents).
  { path: '/studio/:lane(agents|ask|email)?', name: 'studio-list',
    component: () => import('@/views/ConversationsView.vue'),
    meta: { role: 'member' } },
  // Back-compat: the surface used to live at /chat (list) and /chat/:id
  // (thread). Both now sit under /studio. Forward the old deep-links so
  // any bookmark / Slack-side reference keeps working.
  { path: '/chat', redirect: { name: 'studio-list' } },
  { path: '/chat/:id', redirect: to => ({ name: 'studio', params: { id: to.params.id } }) },
  // Specialists is an admin management surface (install / configure /
  // set-default / disable agents) - gated to admins. Members discover
  // and talk to agents via the New-thread picker and @hq, not here.
  // The server enforces the same boundary (agents_api mutations call
  // conv_access::require_admin); this route gate is the UX half.
  // Agents directory (admin). NOTE: route *names* stay 'specialists' /
  // 'specialist-*' as stable internal ids; the user-facing label + path
  // are 'Agents' / '/agents'.
  { path: '/agents', name: 'specialists',
    component: () => import('@/views/AgentsView.vue'),
    meta: { role: 'admin' } },
  // Add / edit an agent on a full page (not a modal - the form is long
  // and a panel can't hold it). `/new` is static so it must precede the
  // `:id` param route. Both render the same editor in create vs edit
  // mode, keyed off the presence of the id prop.
  { path: '/agents/new', name: 'specialist-new',
    component: () => import('@/views/AgentEditorView.vue'),
    meta: { role: 'admin' } },
  { path: '/agents/:id/edit', name: 'specialist-edit',
    component: () => import('@/views/AgentEditorView.vue'),
    meta: { role: 'admin' }, props: true },
  // Back-compat: the page used to live at /specialists (and /agents was a
  // redirect to it). Forward old deep-links to the new canonical paths.
  { path: '/specialists', redirect: { name: 'specialists' } },
  { path: '/specialists/new', redirect: { name: 'specialist-new' } },
  { path: '/specialists/:id/edit',
    redirect: to => ({ name: 'specialist-edit', params: { id: to.params.id } }) },
  // Admin-only: enabling a skill affects the whole workspace (every
  // agent), and the backend require_admin's the skill mutations.
  { path: '/skills', name: 'skills',
    component: () => import('@/views/SkillsView.vue'),
    meta: { role: 'admin' } },
  // Author a workspace skill on a full page (not a modal - same pattern
  // as agents/schedules). `/new` is static so it would precede any future
  // `:id` param route.
  { path: '/skills/new', name: 'skill-new',
    component: () => import('@/views/SkillEditorView.vue'),
    meta: { role: 'admin' } },
  // View / edit one skill's SKILL.md on a full page. Declared AFTER
  // `/skills/new` so the static segment isn't captured by `:slug`.
  { path: '/skills/:slug', name: 'skill-detail',
    component: () => import('@/views/SkillDetailView.vue'),
    meta: { role: 'admin' }, props: true },
  // Full-bleed: the Files page owns its column edge-to-edge (its own
  // header bar + a resizable list/preview split), like Studio. No outer
  // padding, no #app-header - it renders its own header. Two tabs:
  // uploaded Documents + agent-produced Generated artifacts.
  { path: '/files', name: 'files',
    component: () => import('@/views/DocumentsView.vue'),
    meta: { role: 'member', fullBleed: true } },
  // The standalone doc-primary editor (/files/:id/edit) is retired: documents
  // now open INSIDE Studio as a Scriptor column next to the conversation that
  // drives them (DocumentsView.openEditor routes to `studio` with ?doc=<id>).
  { path: '/documents', redirect: { name: 'files' } },
  { path: '/apps', name: 'apps',
    component: () => import('@/views/AppsView.vue'),
    meta: { role: 'member' } },
  // Tables: dynamic structured data (docs/tables.md). List + grid detail.
  // Member surface - the API capability-gates tables:read/write.
  { path: '/tables', name: 'tables',
    component: () => import('@/views/TablesView.vue'),
    meta: { role: 'member' } },
  { path: '/tables/:id', name: 'table-detail',
    component: () => import('@/views/TableDetailView.vue'),
    meta: { role: 'member' }, props: true },
  // Studio: chat-left + (future) preview-right for one conversation.
  // Linked from Slack's "Open Studio" Block Kit reply on app promotion
  // and from the apps list. id is the conversation UUID; surface-id
  // entry point lives in a follow-up commit.
  { path: '/studio/:id', name: 'studio',
    component: () => import('@/views/StudioView.vue'),
    // Full-bleed: studio fills the shell content column edge-to-edge
    // (no max-width cap, no outer padding). It owns its own scroll
    // regions per pane so the page itself never scrolls.
    meta: { role: 'member', fullBleed: true }, props: true },
  { path: '/schedules', name: 'schedules',
    component: () => import('@/views/SchedulesView.vue'),
    meta: { role: 'member' } },
  // Add / edit a schedule on a full page. `/new` is static so it must
  // precede the `:id` param route; both render the same editor.
  { path: '/schedules/new', name: 'schedule-new',
    component: () => import('@/views/ScheduleEditorView.vue'),
    meta: { role: 'member' } },
  { path: '/schedules/:id/edit', name: 'schedule-edit',
    component: () => import('@/views/ScheduleEditorView.vue'),
    meta: { role: 'member' }, props: true },
  { path: '/inbox', name: 'inbox',
    component: () => import('@/views/InboxView.vue'),
    meta: { role: 'member' } },
  // Run-visibility surface: a global "what's running / what ran" list, and
  // per-schedule history via ?schedule=<id>.
  { path: '/activity', name: 'activity',
    component: () => import('@/views/ActivityView.vue'),
    meta: { role: 'member' } },
  // Member-facing review queue: replies to the team/default sender that were
  // assigned to this user. The admin equivalent is /admin/email (role:admin);
  // this surface is scoped by the API to the caller's own review_reply items.
  { path: '/email', name: 'email-replies',
    component: () => import('@/views/EmailRepliesView.vue'),
    meta: { role: 'member' } },
  // Member full-message viewer: reuses the admin EmailMessageView component
  // (body + attachments). The content/attachment APIs are owner-or-admin
  // scoped, so a member only opens a message attributed/bound to them.
  { path: '/email/messages/:id', name: 'email-message',
    component: () => import('@/views/admin/EmailMessageView.vue'),
    meta: { role: 'member', fullBleed: true }, props: true },
  { path: '/developer/apps', name: 'developer-apps',
    component: () => import('@/views/DeveloperAppsView.vue'),
    meta: { role: 'member' } },

  // Help & Docs - browse (search + categories) and individual articles. The
  // article slug is flat (/help/<slug>); cross-links in article markdown use
  // the same shape. Member-accessible (everyone needs help).
  { path: '/help', name: 'help',
    component: () => import('@/views/HelpView.vue'),
    meta: { role: 'member' } },
  { path: '/help/:slug', name: 'help-article',
    component: () => import('@/views/HelpView.vue'),
    meta: { role: 'member' }, props: true },

  { path: '/me', name: 'me',
    component: () => import('@/views/MeView.vue'),
    meta: { role: 'member' } },
  { path: '/notifications', name: 'notifications',
    component: () => import('@/views/NotificationsView.vue'),
    meta: { role: 'member' } },
  // Connect surface: add HQ to your tools + act on the profiling
  // recommendations (enable integrations/skills). ADMIN - everything it does
  // (enabling integrations/skills) is an admin action, so it lives in the
  // Admin nav, not the account menu. The onboarding wizard links here too.
  { path: '/connect', name: 'connect',
    component: () => import('@/views/ConnectView.vue'),
    meta: { role: 'admin' } },

  // First-run onboarding wizard (Onboarding v2 P3). Member route - the
  // soft-gate in beforeEach steers brand-new tenants here until they finish.
  { path: '/onboarding', name: 'onboarding',
    component: () => import('@/views/OnboardingView.vue'),
    meta: { role: 'member' } },

  // ─── Admin-only surface ─────────────────────────────────
  { path: '/admin/schedules', name: 'admin-schedules',
    component: () => import('@/views/admin/WorkspaceSchedulesView.vue'),
    meta: { role: 'admin' } },
  // Admin create/edit reuse the same full-page ScheduleEditorView as the
  // personal routes; `meta.scope: 'workspace'` flips it to load from the
  // workspace collection and return to the admin list. `/new` is static so
  // it precedes the `:id` param route.
  { path: '/admin/schedules/new', name: 'admin-schedule-new',
    component: () => import('@/views/ScheduleEditorView.vue'),
    meta: { role: 'admin', scope: 'workspace' } },
  { path: '/admin/schedules/:id/edit', name: 'admin-schedule-edit',
    component: () => import('@/views/ScheduleEditorView.vue'),
    meta: { role: 'admin', scope: 'workspace' }, props: true },
  // Each tab is its own URL (`/admin/integrations/built-in|system|custom`) so
  // back/forward + reload + deep-links preserve it. One param route -> one
  // record, so the nav link (to: '/admin/integrations') stays active on every
  // tab. Bare `/admin/integrations` defaults to Built-in (the view canonicalises).
  { path: '/admin/integrations/:tab(built-in|system|custom|discover)?', name: 'admin-integrations',
    component: () => import('@/views/admin/IntegrationsView.vue'),
    meta: { role: 'admin' } },
  // Tabbed: Slack | Teams. Each tab is its own URL so back/forward + reload +
  // deep-links preserve it; the nav link (to: '/admin/channels') stays active on
  // every tab. Bare `/admin/channels` defaults to a connected channel (the view
  // canonicalises).
  { path: '/admin/channels/:tab(slack|teams)?', name: 'admin-channels',
    component: () => import('@/views/admin/ChannelsView.vue'),
    meta: { role: 'admin' } },
  // Email oversight + triage (Activity / Review / Endpoints). Admin: the
  // quarantined / unrouted queues are an admin surface and the triage API
  // require_admin's the mutations - this route gate is the UX half.
  // Each tab is its own URL (`/admin/email/review|threads|activity|endpoints`)
  // so back/forward + reload + deep-links preserve the tab. One param route ->
  // one record, so the nav link (to: '/admin/email') stays active on every tab.
  // Bare `/admin/email` defaults to Review (EmailView canonicalises the URL).
  { path: '/admin/email/:tab(review|threads|activity|endpoints)?', name: 'admin-email',
    component: () => import('@/views/admin/EmailView.vue'),
    meta: { role: 'admin' }, props: true },
  // Endpoint editor (create + edit), full page like the agent/schedule
  // editors. `/new` is static so it must precede the `:id` param route.
  { path: '/admin/email/endpoints/new', name: 'admin-email-endpoint-new',
    component: () => import('@/views/admin/EmailEndpointEditorView.vue'),
    meta: { role: 'admin' } },
  { path: '/admin/email/endpoints/:id/edit', name: 'admin-email-endpoint-edit',
    component: () => import('@/views/admin/EmailEndpointEditorView.vue'),
    meta: { role: 'admin' }, props: true },
  // Read-only message viewer (the whole EML: headers + body + attachments),
  // full page like the editor. `messages` / `quarantine` aren't in the :tab
  // enum above, and these carry a second segment, so there's no collision with
  // the tab route. Quarantined mail has no conversation, so this view is the
  // only way to read it before accept/reject. `?from=<tab>` drives Back.
  // Full-bleed: a Studio-like split (email left, preview right) owns the
  // content column edge-to-edge and renders its own top bar.
  { path: '/admin/email/messages/:id', name: 'admin-email-message',
    component: () => import('@/views/admin/EmailMessageView.vue'),
    meta: { role: 'admin', fullBleed: true } },
  { path: '/admin/email/quarantine/:id', name: 'admin-email-quarantine',
    component: () => import('@/views/admin/EmailMessageView.vue'),
    meta: { role: 'admin', fullBleed: true } },
  { path: '/admin/audit', name: 'admin-audit',
    component: () => import('@/views/admin/AuditView.vue'),
    meta: { role: 'admin' } },
  { path: '/admin/users', name: 'admin-users',
    component: () => import('@/views/admin/UsersView.vue'),
    meta: { role: 'admin' } },
  // One user on a full page: the sign-in + admin-role controls and the
  // erase / invite lifecycle live here (they were too wide for the list).
  // `:id` is the workspace_users UUID; declared after the static `/admin/users`
  // so the list isn't captured by the param.
  { path: '/admin/users/:id', name: 'admin-user',
    component: () => import('@/views/admin/UserDetailView.vue'),
    meta: { role: 'admin' }, props: true },
  { path: '/admin/settings', name: 'admin-settings',
    component: () => import('@/views/admin/SettingsView.vue'),
    meta: { role: 'admin' } },
  { path: '/admin/usage', name: 'admin-usage',
    component: () => import('@/views/admin/UsageView.vue'),
    meta: { role: 'admin' } },
  { path: '/admin/business-profile', name: 'admin-business-profile',
    component: () => import('@/views/admin/BusinessProfileView.vue'),
    meta: { role: 'admin' } },

  // ─── Operator back-office (HQ platform team only) ───────
  // Gated by `meta.role: 'operator'` -> auth.isOperator (an admin of an
  // is_platform tenant; today `tic`). The server enforces the same boundary
  // on /v1/admin/* via platform_admin::require_operator - this route gate is
  // only the UX half. Tabbed (signups / invites / tenants); the bare
  // /operator path canonicalises to the signups tab inside the view.
  { path: '/operator/:tab(signups|invites|tenants)?', name: 'operator',
    component: () => import('@/views/operator/OperatorView.vue'),
    meta: { role: 'operator' }, props: true },
  // Design-system gallery: every primitive in its states on a live theme
  // toggle, to review the "2026" language + catch off-token drift. Any signed-in
  // user can reach it; no nav entry, found by URL. `components` is outside the
  // operator tab enum above, so it gets its own route rather than being captured
  // as a tab.
  { path: '/operator/components', name: 'operator-components',
    component: () => import('@/views/ComponentGalleryView.vue') },

  // ─── Catch-all ─────────────────────────────────────────
  { path: '/:pathMatch(.*)*', name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true } },
];

export const router = createRouter({
  // Dynamic base: when the URL is workspace-scoped (`/w/<slug>/…`) the router
  // operates RELATIVE to that prefix, so every route/name/link below stays
  // workspace-agnostic while the URL stays workspace-scoped. See lib/workspace.
  history: createWebHistory(historyBase()),
  routes,
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (to.meta.public) return true;
  // On the very first gated navigation the SPA hasn't asked the
  // server who the user is yet. Wait for that single fetch instead
  // of bouncing to /signin based on uninitialised state -- otherwise
  // a deep-link to /studio/<id> with a valid hq-session cookie
  // ends up at /signin even though the cookie would resolve fine.
  // (`ensureHydrated` hits /me with the active-workspace header, so
  // `auth.user.tenant.slug` is the SERVER-resolved active workspace.)
  await auth.ensureHydrated();
  if (!auth.isAuthenticated) {
    // Sign-in lives at the root (workspace-less) and is reached by a full
    // navigation, not a router push - a `{name:'signin'}` push would resolve
    // UNDER the current `/w/<slug>/` base and 404. Hard-redirect, preserving
    // the deep link so the post-sign-in landing can honour it.
    window.location.assign(`/signin?next=${encodeURIComponent(window.location.pathname)}`);
    return false;
  }

  // ── URL-scoped workspace reconciliation ────────────────────────────────
  // The active workspace is whatever the server resolved for this request's
  // `X-Hq-Workspace` (the slug in the URL): valid -> it echoes back; invalid /
  // not-a-member -> the server fell back to the caller's default. Bring the URL
  // in line with the truth via a full navigation (which re-bases the router).
  const urlWs = currentWorkspace();
  const activeWs = auth.user?.tenant.slug ?? null;
  if (activeWs) {
    if (!urlWs) {
      // Workspace-less authenticated URL (bare `/`, a stale bookmark): land the
      // user in their resolved workspace, preserving the in-app sub-path.
      window.location.assign(workspacePath(activeWs, to.fullPath));
      return false;
    }
    if (urlWs !== activeWs) {
      // URL named a workspace the caller can't reach; the server fell back.
      window.location.assign(workspacePath(activeWs, '/'));
      return false;
    }
  }
  if (to.meta.role === 'admin' && !auth.isAdmin) {
    return { name: 'studio-list' };
  }
  // Operator back-office: only an admin of an is_platform tenant. A non-operator
  // who deep-links /operator gets bounced (the API would 403 them anyway).
  if (to.meta.role === 'operator' && !auth.isOperator) {
    return { name: 'studio-list' };
  }
  // First-run soft-gate: steer brand-new tenants (onboarding not completed)
  // into the wizard. Existing tenants are completed=true so they're never
  // touched. Skip when already heading to the wizard or to a public route, so
  // there's no redirect loop.
  if (
    auth.isAuthenticated &&
    auth.user?.onboarding &&
    auth.user.onboarding.completed === false &&
    to.name !== 'onboarding' &&
    !to.meta.public
  ) {
    return { name: 'onboarding' };
  }
  return true;
});
